import { useEffect, useRef } from "react";
import { useMap, useMapEvents } from "react-leaflet";
import { Hand, Lock, Maximize2, Minimize2 } from "lucide-react";

const HANDLERS = ["dragging", "touchZoom", "scrollWheelZoom", "doubleClickZoom", "boxZoom", "keyboard"];

// Map Interaction Lock: saat terkunci, gesture navigasi Leaflet dimatikan sehingga scroll/swipe menggulir halaman.
export function MapLockHandlers({ locked, onUnlock, fullscreen }) {
  const map = useMap();
  const down = useRef(null);
  useEffect(() => {
    HANDLERS.forEach((h) => h !== "doubleClickZoom" && map[h]?.[locked ? "disable" : "enable"]());
    if (locked) { map.doubleClickZoom.disable(); return undefined; }
    // Tunda dblclick-zoom agar klik pembuka kunci + klik kedua tidak langsung memperbesar peta.
    const t = setTimeout(() => map.doubleClickZoom.enable(), 400);
    return () => clearTimeout(t);
  }, [map, locked]);
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 60);
    return () => clearTimeout(t);
  }, [map, fullscreen]);
  useEffect(() => {
    const el = map.getContainer();
    const rec = (e) => { down.current = { x: e.clientX, y: e.clientY }; };
    el.addEventListener("pointerdown", rec);
    return () => el.removeEventListener("pointerdown", rec);
  }, [map]);
  // Hanya ketukan/klik sungguhan (gerak < 6px) yang membuka kunci; drag/usap tidak.
  useMapEvents({ click: (e) => {
    const d = down.current, o = e.originalEvent;
    if (locked && (!d || Math.hypot(o.clientX - d.x, o.clientY - d.y) < 6)) onUnlock();
  } });
  return null;
}

const BTN = "absolute z-[450] flex items-center gap-1.5 rounded-md border border-[#D1D5DB] bg-white px-3 py-2 text-xs font-bold text-[#111827] shadow-md transition-colors hover:bg-[#F3F4F6]";

export const MapLockOverlay = ({ locked, canLock, onLock, fullscreen, onFullscreen }) => (
  <>
    {fullscreen && <style>{`.leaflet-top{padding-top:env(safe-area-inset-top)}.leaflet-left{padding-left:env(safe-area-inset-left)}.leaflet-bottom{padding-bottom:env(safe-area-inset-bottom)}`}</style>}
    {canLock && onFullscreen && (
      <button type="button" onClick={() => onFullscreen(!fullscreen)} className={`${BTN} right-3 top-3`} style={fullscreen ? { top: "max(0.75rem, env(safe-area-inset-top))", right: "max(0.75rem, env(safe-area-inset-right))" } : undefined}
              aria-label={fullscreen ? "Keluar layar penuh" : "Layar penuh"} data-testid="map-fullscreen-btn">
        {fullscreen ? <Minimize2 className="h-3.5 w-3.5" aria-hidden /> : <Maximize2 className="h-3.5 w-3.5" aria-hidden />}
        {fullscreen ? "Keluar" : "Layar Penuh"}
      </button>
    )}
    {locked && (
      <div className="pointer-events-none absolute inset-x-0 bottom-8 z-[450] flex justify-center px-3 sm:bottom-auto sm:top-3 sm:px-36" data-testid="map-lock-hint">
        <span className="flex items-center gap-1.5 rounded-full bg-[#111827]/80 px-3 py-1.5 text-xs font-medium text-white shadow-lg backdrop-blur-sm">
          <Hand className="h-3.5 w-3.5" aria-hidden />Ketuk peta untuk mengaktifkan interaksi
        </span>
      </div>
    )}
    {!locked && canLock && (
      <button type="button" onClick={onLock} data-testid="map-lock-btn"
              className={`${BTN} bottom-6 left-3`} style={fullscreen ? { bottom: "max(1.5rem, calc(env(safe-area-inset-bottom) + 0.75rem))" } : undefined}>
        <Lock className="h-3.5 w-3.5" aria-hidden />Kunci Peta
      </button>
    )}
  </>
);
